/**
 * Individual KPI cell for Cached Performance (CR-4751).
 *
 * Mirrors DefaultTab KPIMetricsSection cell styling (label, big value, footer
 * with delta indicator) and adds a sparkline mini-chart from the trend series.
 */
import { LineChart, Line, ResponsiveContainer } from 'recharts'
import { Info, TrendingUp, TrendingDown, Minus } from 'lucide-react'

import type {
  DashboardSnapshot, TrendPoint,
} from '../../../lib/api/dashboard-snapshots'
import { useFormatters } from '../../../contexts/PreferencesContext'
import {
  NA_LABEL, NA_REASON, asNumber, deltaTone, formatValueByUnit, percentDelta, toChartSeries,
} from './helpers'

interface CachedKPICellProps {
  label: string
  snapshot: DashboardSnapshot | undefined
  trend: TrendPoint[] | undefined
  lowerIsBetter?: boolean
  /**
   * CR-4751: the value's badge colour — PPM's grade band, as on the live card.
   * PPM used to sit in an always-red badge, 0 ppm included.
   */
  badgeColor?: string
  /** CR-4751: the latest period had nothing to measure — show N/A, not "building history". */
  notApplicable?: boolean
}

const TONE_TEXT = { good: 'text-green-600', bad: 'text-red-600', flat: 'text-muted-foreground' }
const TONE_LINE = { good: '#10b981', bad: '#ef4444', flat: '#6b7280' }

function isPartialPeriod(snapshot: DashboardSnapshot): boolean {
  if (!snapshot.period_end) return false
  const periodEnd = new Date(snapshot.period_end + 'T23:59:59')
  return periodEnd > new Date()
}

function toneOf(change: number, lowerIsBetter: boolean): keyof typeof TONE_TEXT {
  const { good } = deltaTone(change, lowerIsBetter)
  if (good === null) return 'flat'
  return good ? 'good' : 'bad'
}

function arrowFor(change: number) {
  const { direction } = deltaTone(change, false)
  if (direction === 'up') return <TrendingUp className="w-3 h-3" />
  if (direction === 'down') return <TrendingDown className="w-3 h-3" />
  return <Minus className="w-3 h-3" />
}

export function CachedKPICell({
  label, snapshot, trend, lowerIsBetter = false, badgeColor, notApplicable = false,
}: CachedKPICellProps) {
  const { formatCurrency, formatCompactCurrency, formatNumber } = useFormatters()
  const labelClass = 'text-xs text-muted-foreground font-medium uppercase tracking-wide leading-tight h-8 flex items-center justify-center text-center px-1'
  const valueClass = 'h-16 flex items-center justify-center w-full min-w-0'
  const sparkClass = 'h-8 w-full'
  const footClass = 'h-6 flex items-center justify-center'

  if (!snapshot) {
    return (
      <div className="flex flex-col items-center min-w-0">
        <p className={labelClass}>{label}</p>
        <div className={valueClass}>
          <p className="text-2xl font-semibold text-muted-foreground">{notApplicable ? NA_LABEL : '—'}</p>
        </div>
        <div className={sparkClass} />
        <div className={footClass}>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Info className="w-3 h-3" /> {notApplicable ? NA_REASON : 'Building history'}
          </span>
        </div>
      </div>
    )
  }

  const trendPoints = trend ?? []
  const sparkSeries = toChartSeries(trendPoints)
  const current = asNumber(snapshot.metric_value)
  const previousPoint = trendPoints.length >= 2 ? trendPoints[trendPoints.length - 2] : undefined
  const hasPrior = previousPoint !== undefined
  const previous = hasPrior ? asNumber(previousPoint!.metric_value) : null
  const delta = previous !== null && previous !== 0
    ? percentDelta(current, previous)
    : null

  // CR-4751: the arrow follows the number, the colour whether that is good
  // news — Appeals down 100% showed a green UP arrow.
  const tone = delta === null ? 'flat' : toneOf(delta, lowerIsBetter)

  // CR-4751: money in compact form (£742K) so three figures fit one card —
  // they overlapped at full length; the exact amount is the hover title. In
  // the currency the API says the figure is in, never the preferred one by default.
  const ccy = snapshot.currency ?? undefined
  const formatted = formatValueByUnit(
    snapshot.metric_value, snapshot.metric_unit, (n) => formatCompactCurrency(n, ccy), (n) => formatNumber(n))
  const exact = formatValueByUnit(
    snapshot.metric_value, snapshot.metric_unit, (n) => formatCurrency(n, ccy), (n) => formatNumber(n))

  const renderFooter = () => {
    if (!hasPrior) {
      return (
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Info className="w-3 h-3" /> no prior
        </span>
      )
    }
    if (previous === 0 && current === 0) {
      return (
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Minus className="w-3 h-3" /> unchanged
        </span>
      )
    }
    if (previous === 0 && current !== 0) {
      return (
        <span className={`text-xs flex items-center gap-1 ${TONE_TEXT[toneOf(current, lowerIsBetter)]}`}>
          {arrowFor(current)} new vs 0 prior
        </span>
      )
    }
    return (
      <span className={`text-xs flex items-center gap-1 ${TONE_TEXT[tone]}`}>
        {arrowFor(delta!)} {delta!.toFixed(1)}% vs prior
      </span>
    )
  }

  const partial = isPartialPeriod(snapshot)

  return (
    <div className="flex flex-col items-center min-w-0">
      <p className={labelClass}>
        {label}
        {partial && (
          <span
            className="ml-1 inline-block px-1 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-semibold align-middle"
            title="This period hasn't ended yet — value is partial"
          >
            PARTIAL
          </span>
        )}
      </p>
      <div className={valueClass} title={exact}>
        {badgeColor ? (
          <div className="text-white px-3 py-2 rounded-md" style={{ backgroundColor: badgeColor }}>
            <p className="text-2xl font-bold leading-none">{formatted}</p>
          </div>
        ) : (
          // Money matches the live KPI cards (text-xl) only from 2xl: three
          // amounts share a third of the row and ran into each other at laptop
          // widths. truncate is the last guard; `title` has the exact amount.
          <div className={`${snapshot.metric_unit === 'currency' ? 'text-lg 2xl:text-xl' : 'text-2xl'} font-bold leading-none text-center max-w-full truncate`}>
            {formatted}
          </div>
        )}
      </div>
      <div className={sparkClass}>
        {sparkSeries.length >= 2 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkSeries} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={TONE_LINE[tone]}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : null}
      </div>
      <div className={footClass}>{renderFooter()}</div>
    </div>
  )
}
